<div class="p-4">
  <mat-dialog-content>
    @if (!loading()) {
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-2">
          @switch (type()) {
            @case (modalType.ManualCreation) {
              <h3>{{ "appointments.headings.create" | translate }}</h3>
            }
            @case (modalType.BindToExisting) {
              <h3>{{ "appointments.headings.bind" | translate }}</h3>
            }
            @case (modalType.Cancel) {
              <h3>{{ "appointments.headings.cancel" | translate }}</h3>
            }
          }
          <button mat-dialog-close>
            <mat-icon
              aria-hidden="true"
              aria-label="Close icon"
              fontIcon="close"
            ></mat-icon>
          </button>
        </div>

        <form [formGroup]="createAppointmentForm">
          <mat-form-field class="w-full">
            <mat-label>{{ "appointments.label.type" | translate }}</mat-label>
            <input
              matInput
              readonly
              [type]="'text'"
              [value]="'medicalServices.prescriptionTypes.' + fc.appointmentType.value | translate"
            />
          </mat-form-field>

          @if (data.prescriptions.length === 1) {
            <mat-form-field class="w-full">
              <mat-label>{{ "appointments.label.name" | translate }}</mat-label>
              <input
                matInput
                readonly
                [type]="'text'"
                [value]="fc.appointmentName.value || ('appointments.label.serviceNotSpecified' | translate)"
              />
            </mat-form-field>
          } @else {
            <div class="mb-4 w-full">
              <mat-label class="text-gray-600 mb-2 text-sm font-medium">{{ "appointments.label.selectedPrescriptions" | translate }}</mat-label>
              <div class="flex max-h-60 flex-col gap-1 overflow-y-auto">
                @for (prescription of data.prescriptions; track prescription.id) {
                  <prescriptions-modal-item
                    [prescription]="{
                      id: prescription.id,
                      name: prescription.name,
                      franchise: prescription.franchise,
                      type: getPrescriptionType(appointmentType()),
                    }"
                  />
                }
              </div>
            </div>
          }

          @if (type() !== modalType.Cancel) {
            <mat-form-field class="w-full">
              <mat-label>{{ "appointments.label.partnerName" | translate }}</mat-label>
              <input
                matInput
                type="text"
                [formControl]="fc.partnerName"
                [matAutocomplete]="partnersAutocomplete"
                [placeholder]="'appointments.label.partnerSearchPlaceholder' | translate"
              />
              @if (fc.partnerName.value) {
                <button
                  matSuffix
                  mat-icon-button
                  type="button"
                  aria-label="Clear"
                  (click)="clearPartnersSearch()"
                >
                  <mat-icon matIconSuffix>close</mat-icon>
                </button>
              }
              <mat-autocomplete
                #partnersAutocomplete
                (optionSelected)="selectPartner($event.option.value)"
              >
                <div
                  sharedInfiniteScroll
                  [scrollLoading]="partnersLoading()"
                  [hasNextPage]="!!partnersContinuationToken()"
                  (fetchNextPage)="fetchPartnersNextPage()"
                  class="max-h-60 overflow-auto"
                >
                  @for (partner of partnerOptions(); track partner.id) {
                    <mat-option [value]="partner">
                      {{ partner.name }}
                    </mat-option>
                  }
                  @if (partnersLoading()) {
                    <div class="flex w-full items-center justify-center">
                      <shared-loader></shared-loader>
                    </div>
                  }
                </div>
              </mat-autocomplete>
              @if (fc.partnerId.invalid && fc.partnerId.touched) {
                <mat-error>{{ "validation.error.required" | translate }}</mat-error>
              }
            </mat-form-field>

            @if (type() === modalType.BindToExisting) {
              @if (fc.partnerId.value) {
                <div>
                  <hm-suggestions-list
                    [prescriptionId]="data.prescriptions[0].id"
                    [partnerId]="fc.partnerId.value"
                    [userId]="data.userId"
                    (suggestionSelected)="selectSuggestion($event)"
                  ></hm-suggestions-list>
                </div>
              } @else {
                <div class="bg-gray-50 text-gray-600 mt-4 rounded-lg p-4 text-center">
                  {{ "appointments.descriptions.selectPartnerFirst" | translate }}
                </div>
              }
            } @else {
              <mat-form-field class="w-full">
                <mat-label>{{ "appointments.label.departmentName" | translate }}</mat-label>
                <input
                  matInput
                  type="text"
                  [formControl]="fc.locationName"
                  [matAutocomplete]="locationsAutocomplete"
                  [placeholder]="'appointments.label.locationSearchPlaceholder' | translate"
                />
                @if (fc.locationName.value) {
                  <button
                    matSuffix
                    mat-icon-button
                    type="button"
                    aria-label="Clear"
                    (click)="clearLocationsSearch()"
                  >
                    <mat-icon matIconSuffix>close</mat-icon>
                  </button>
                }
                <mat-autocomplete
                  #locationsAutocomplete
                  (optionSelected)="selectLocation($event.option.value)"
                >
                  <div
                    sharedInfiniteScroll
                    [scrollLoading]="locationsLoading()"
                    [hasNextPage]="!!locationsContinuationToken()"
                    (fetchNextPage)="fetchLocationsNextPage()"
                    class="max-h-60 overflow-auto"
                  >
                    @for (location of locationOptions(); track location.id) {
                      <mat-option [value]="location">
                        <div class="flex flex-col items-start gap-0.5 py-1">
                          <span>{{ location.name }}</span>
                          <span class="text-sm text-gray-2">{{ location.location?.city }}, {{ location.location?.address }}</span>
                        </div>
                      </mat-option>
                    }
                    @if (locationsLoading()) {
                      <div class="flex w-full items-center justify-center">
                        <shared-loader></shared-loader>
                      </div>
                    }
                  </div>
                </mat-autocomplete>
                @if (fc.locationId.invalid && fc.locationId.touched) {
                  <mat-error>{{ "validation.error.required" | translate }}</mat-error>
                }
              </mat-form-field>

              <mat-form-field class="w-full">
                <mat-label>{{ "appointments.label.doctorName" | translate }}</mat-label>
                <input
                  matInput
                  [type]="'text'"
                  [formControl]="fc.doctorName"
                  [matAutocomplete]="doctorsAutocomplete"
                />
                @if (fc.doctorName.value) {
                  <button
                    matSuffix
                    mat-icon-button
                    type="button"
                    aria-label="Clear"
                    (click)="clearDoctorsSearch()"
                  >
                    <mat-icon matIconSuffix>close</mat-icon>
                  </button>
                }
                <mat-autocomplete
                  #doctorsAutocomplete
                  (optionSelected)="selectDoctor($event.option.value)"
                >
                  <div
                    sharedInfiniteScroll
                    [scrollLoading]="doctorsLoading()"
                    [hasNextPage]="!!doctorsContinuationToken()"
                    (fetchNextPage)="fetchDoctorsNextPage()"
                    class="max-h-60 overflow-auto"
                  >
                    @for (doctor of doctorOptions(); track doctor.id) {
                      <mat-option [value]="doctor">
                        {{ doctor.fullName }}
                      </mat-option>
                    }
                    @if (doctorsLoading()) {
                      <div class="flex w-full items-center justify-center">
                        <shared-loader></shared-loader>
                      </div>
                    }
                  </div>
                </mat-autocomplete>
                @if (fc.doctorName.invalid && fc.doctorName.touched) {
                  <mat-error>{{ "validation.error.required" | translate }}</mat-error>
                }
              </mat-form-field>

              <div class="flex w-full gap-4">
                <mat-form-field>
                  <mat-label>{{ "appointments.label.date" | translate }}</mat-label>
                  <input
                    matInput
                    [matDatepicker]="datepicker"
                    [formControl]="fc.date"
                  />
                  <mat-datepicker #datepicker />
                  <mat-datepicker-toggle
                    [for]="datepicker"
                    matSuffix
                  />
                  @if (fc.date.invalid && fc.date.touched) {
                    <mat-error>{{ "validation.error.required" | translate }}</mat-error>
                  }
                </mat-form-field>

                <mat-form-field>
                  <mat-label>{{ "location.label.from" | translate }}</mat-label>
                  <input
                    matInput
                    [matTimepicker]="fromTimepicker"
                    [formControl]="fc.startTime"
                  />
                  <mat-timepicker #fromTimepicker />
                  <mat-timepicker-toggle
                    matSuffix
                    [for]="fromTimepicker"
                  />

                  @if (fc.startTime.invalid && fc.startTime.touched) {
                    <mat-error>{{ "validation.error.required" | translate }}</mat-error>
                  }
                </mat-form-field>

                <mat-form-field>
                  <mat-label>{{ "location.label.to" | translate }}</mat-label>
                  <input
                    matInput
                    [matTimepicker]="toTimepicker"
                    [formControl]="fc.endTime"
                  />
                  <mat-timepicker #toTimepicker />
                  <mat-timepicker-toggle
                    matSuffix
                    [for]="toTimepicker"
                  />
                  @if (fc.startTime.invalid && fc.startTime.touched) {
                    <mat-error>{{ "validation.error.required" | translate }}</mat-error>
                  }
                </mat-form-field>
              </div>

              <mat-form-field class="w-full">
                <mat-label>{{ "appointments.label.serviceCostLabel" | translate }}</mat-label>
                <input
                  matInput
                  type="number"
                  min="0"
                  step="0.01"
                  [formControl]="fc.price"
                />
                @if (fc.price.invalid && fc.price.touched) {
                  <mat-error>{{ "validation.error.required" | translate }}</mat-error>
                }
              </mat-form-field>
            }
          } @else {
            <shared-warning-message
              [text]="'appointments.descriptions.cancellationWarning' | translate: { partner: partnerName(), location: address() }"
              [subText]="'appointments.descriptions.cancellationDetails' | translate: { dateTime: startDate() }"
            ></shared-warning-message>
          }
        </form>
      </div>
    } @else {
      <div class="flex items-center justify-center">
        <shared-loader></shared-loader>
      </div>
    }
  </mat-dialog-content>
  <mat-dialog-actions class="gap-4">
    @switch (type()) {
      @case (modalType.Cancel) {
        <button
          class="btn btn-primary"
          [type]="'button'"
          (click)="submit()"
        >
          {{ "prescription.action.cancelAppointment" | translate }}
        </button>
      }
      @default {
        <button
          class="btn btn-secondary"
          mat-dialog-close
          [type]="'button'"
        >
          {{ "buttons.cancel" | translate }}
        </button>
        <button
          class="btn btn-primary"
          [type]="'button'"
          [disabled]="createAppointmentForm.invalid && type() === modalType.BindToExisting ? !selectedSuggestion() : false"
          (click)="submit()"
        >
          {{ "buttons.confirm" | translate }}
        </button>
      }
    }
  </mat-dialog-actions>
</div>
